import { useCallback, useEffect, useState } from "react";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import {
  VitalsParameter,
  VitalsReading,
  UpdateReadingDto,
} from "@/types/vitals";
import { PAGE_SIZE } from "@/utils/constants";

export function useVitalsReadings(
  memberId: string,
  parameter: VitalsParameter
) {
  const [readings, setReadings] = useState<VitalsReading[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!memberId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await vitalsTrackingService.getReadings(
        memberId,
        parameter,
        1,
        PAGE_SIZE
      );
      setReadings(result.data);
      setTotal(result.total);
      setPage(1);
      setHasMore(
        result.data.length === PAGE_SIZE && result.data.length < result.total
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch readings");
    } finally {
      setIsLoading(false);
    }
  }, [memberId, parameter]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const result = await vitalsTrackingService.getReadings(
        memberId,
        parameter,
        nextPage,
        PAGE_SIZE
      );
      const newData = result.data || [];
      setReadings((prev) => [...prev, ...newData]);
      setTotal(result.total);
      setPage(nextPage);
      setHasMore(
        newData.length === PAGE_SIZE &&
          readings.length + newData.length < result.total
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch more readings");
    } finally {
      setIsLoadingMore(false);
    }
  }, [memberId, parameter, page, isLoadingMore, hasMore, readings.length]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const deleteReading = useCallback(
    async (readingId: string) => {
      await vitalsTrackingService.deleteReading(memberId, readingId);
      await fetch();
    },
    [memberId, fetch]
  );

  const updateReading = useCallback(
    async (readingId: string, dto: UpdateReadingDto) => {
      await vitalsTrackingService.updateReading(memberId, readingId, dto);
      await fetch();
    },
    [memberId, fetch]
  );

  return {
    readings,
    total,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error,
    refresh: fetch,
    deleteReading,
    updateReading,
  };
}
